<template>
  <main class="development-evaluation-import">
    <CompanyBreadcrumb
      :aria-label="$t('general.development_evaluation')"
      :home-aria-label="$t('general.home')"
      :items="breadcrumbItems"
    />

    <div class="page-heading">
      <div>
        <h2>{{ $t("general.add_with_excell") }}</h2>
        <p>{{ $t("general.development_evaluation_import_description") }}</p>
      </div>
      <router-link
        :to="{ name: 'company.development-evaluation.index' }"
        class="btn btn-outline-secondary"
      >
        <i class="uil-arrow-left"></i> {{ $t("general.back") }}
      </router-link>
    </div>

    <section class="card import-card">
      <div class="card-body">
        <label for="development-evaluation-file" class="file-label">
          {{ $t("general.select_excel_file") }} <span class="required">*</span>
        </label>
        <input
          id="development-evaluation-file"
          ref="fileInput"
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          class="form-control-file"
          :disabled="isImporting || isReading"
          @change="selectFile"
        />
        <small class="form-text text-muted">{{
          $t("general.development_evaluation_import_requirements")
        }}</small>
        <div class="template-actions">
          <button
            class="btn btn-info"
            type="button"
            :disabled="isDownloadingTemplate || isImporting"
            @click="downloadTemplate"
          >
            <i
              :class="
                isDownloadingTemplate ? 'uil-spinner-alt' : 'uil-download-alt'
              "
            ></i>
            {{ $t("general.download_excel_template") }}
          </button>
          <p class="template-note">
            {{ $t("general.development_evaluation_import_template_note") }}
          </p>
        </div>

        <div v-if="isReading" class="loading-state" role="status">
          <i class="uil-spinner-alt"></i>
          {{ $t("general.development_evaluation_import_reading") }}
        </div>

        <div
          v-if="errorMessage"
          class="uk-alert-danger mt-3"
          role="alert"
          uk-alert
        >
          <p class="mb-1">{{ errorMessage }}</p>
          <ul v-if="errorDetails.length" class="validation-errors mb-0">
            <li
              v-for="(error, index) in errorDetails"
              :key="`import-error-${index}`"
            >
              {{ error }}
            </li>
          </ul>
        </div>
        <div
          v-if="successMessage"
          class="uk-alert-success mt-3"
          role="status"
          uk-alert
        >
          {{ successMessage }}
        </div>

        <template v-if="file && !isReading && !errorMessage">
          <div class="file-summary">
            <i class="uil-file-check-alt"></i>
            <span
              ><strong>{{ file.name }}</strong> ·
              {{
                $t("general.development_evaluation_import_row_count", {
                  count: rows.length,
                })
              }}</span
            >
          </div>

          <div class="preview-heading">
            <h4>{{ $t("general.development_evaluation_import_preview") }}</h4>
            <span>{{
              $t("general.development_evaluation_import_validation_hint")
            }}</span>
          </div>
          <div class="preview-table-wrap">
            <table class="table table-sm table-striped preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th
                    v-for="(header, index) in headers"
                    :key="`preview-header-${index}`"
                  >
                    {{ header }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, rowIndex) in previewRows"
                  :key="`preview-row-${rowIndex}`"
                >
                  <td>{{ rowIndex + 1 }}</td>
                  <td
                    v-for="(header, columnIndex) in headers"
                    :key="`preview-${rowIndex}-${columnIndex}`"
                  >
                    {{ displayCell(row[columnIndex]) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-if="rows.length > previewRows.length" class="preview-note">
            {{
              $t("general.development_evaluation_import_preview_count", {
                shown: previewRows.length,
                total: rows.length,
              })
            }}
          </p>

          <div class="actions">
            <button
              class="btn btn-primary"
              type="button"
              :disabled="!canImport || isImporting"
              @click="importFile"
            >
              <i v-if="isImporting" class="uil-spinner-alt"></i>
              <i v-else class="uil-upload-alt"></i>
              {{
                isImporting
                  ? $t("general.development_evaluation_importing")
                  : $t("general.development_evaluation_import_button")
              }}
            </button>
            <button
              class="btn btn-light"
              type="button"
              :disabled="isImporting"
              @click="clearFile"
            >
              {{ $t("general.cancel") }}
            </button>
          </div>
        </template>
      </div>
    </section>
  </main>
</template>

<script>
import readXlsxFile from "read-excel-file";
import ApiService from "@/core/services/api.service";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";

const EXPECTED_HEADERS = [
  "Yetkinlik",
  "Çalışan",
  "Çalışan Email",
  "Yöneticisi",
  "Yönetici Email",
  "Yorum Sahibi",
  "Yorum Sahibi Email",
  "Gelişim Alanı/Yorum",
  "Tip",
  "Ay",
  "Yıl",
  "Sıralama",
  "Herkes Tarafından Görülsün mü?",
];

export default {
  name: "DevelopmentEvaluationImport",
  components: { CompanyBreadcrumb },
  data() {
    return {
      file: null,
      headers: [],
      rows: [],
      isReading: false,
      isImporting: false,
      isDownloadingTemplate: false,
      errorMessage: "",
      errorDetails: [],
      successMessage: "",
    };
  },
  computed: {
    breadcrumbItems() {
      return [
        {
          name: this.$t("general.development_evaluation"),
          to: { name: "company.development-evaluation.index" },
        },
        { name: this.$t("general.add_with_excell") },
      ];
    },
    previewRows() {
      return this.rows.slice(0, 10);
    },
    canImport() {
      return Boolean(
        this.file &&
          this.rows.length &&
          this.headers.length === EXPECTED_HEADERS.length
      );
    },
  },
  methods: {
    async downloadTemplate() {
      if (this.isDownloadingTemplate) return;
      this.isDownloadingTemplate = true;
      let objectUrl = null;
      let link = null;
      try {
        const response = await ApiService.get(
          "api/competencies-evaluation/import/template",
          "",
          { responseType: "blob" }
        );
        objectUrl = window.URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        link = document.createElement("a");
        link.href = objectUrl;
        link.download = "development-evaluation-import-template.xlsx";
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        this.errorMessage = this.$t(
          "general.development_evaluation_import_failure"
        );
      } finally {
        link?.remove();
        if (objectUrl) window.URL.revokeObjectURL(objectUrl);
        this.isDownloadingTemplate = false;
      }
    },
    async selectFile(event) {
      const file = event.target.files && event.target.files[0];
      this.resetMessages();
      this.file = null;
      this.headers = [];
      this.rows = [];
      if (!file) return;

      if (!/\.xlsx$/i.test(file.name)) {
        this.errorMessage = this.$t(
          "general.development_evaluation_import_extension_error"
        );
        event.target.value = "";
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        this.errorMessage = this.$t(
          "general.development_evaluation_import_size_error"
        );
        event.target.value = "";
        return;
      }

      this.isReading = true;
      try {
        const data = await readXlsxFile(file, { sheet: "Final" });
        if (!Array.isArray(data) || data.length < 2) {
          throw new Error(
            this.$t("general.development_evaluation_import_final_error")
          );
        }
        const actualHeaders = EXPECTED_HEADERS.map((_, index) =>
          String(data[0]?.[index] ?? "").trim()
        );
        const mismatches = EXPECTED_HEADERS.flatMap((expected, index) =>
          actualHeaders[index] === expected
            ? []
            : [
                this.$t(
                  "general.development_evaluation_import_header_cell_error",
                  {
                    cell: `${String.fromCharCode(65 + index)}1`,
                    header: expected,
                  }
                ),
              ]
        );
        if (mismatches.length) {
          throw new Error(
            `${this.$t(
              "general.development_evaluation_import_headers_error"
            )} ${mismatches.join(" ")}`
          );
        }

        const dataRows = data
          .slice(1)
          .filter((row) =>
            row.some((cell) => cell !== null && String(cell).trim() !== "")
          );
        if (!dataRows.length)
          throw new Error(
            this.$t("general.development_evaluation_import_empty_error")
          );
        this.file = file;
        this.headers = EXPECTED_HEADERS;
        this.rows = dataRows;
      } catch (error) {
        this.errorMessage =
          error?.message ||
          this.$t("general.development_evaluation_import_read_error");
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      } finally {
        this.isReading = false;
      }
    },
    async importFile() {
      if (!this.canImport || this.isImporting) return;
      this.resetMessages();
      this.isImporting = true;
      const formData = new FormData();
      formData.append("file", this.file);
      try {
        await ApiService.post("api/competencies-evaluation/import", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.successMessage = this.$t(
          "general.development_evaluation_import_success"
        );
        this.file = null;
        this.rows = [];
        this.headers = [];
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      } catch (error) {
        this.errorMessage = this.$t(
          "general.development_evaluation_import_failure"
        );
        const details = error?.response?.data?.errors;
        const values = Array.isArray(details)
          ? details
          : Object.values(details || {});
        this.errorDetails = values
          .flatMap((value) => (Array.isArray(value) ? value : [value]))
          .filter(Boolean);
      } finally {
        this.isImporting = false;
      }
    },
    clearFile() {
      if (this.isImporting) return;
      this.file = null;
      this.headers = [];
      this.rows = [];
      this.resetMessages();
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    resetMessages() {
      this.errorMessage = "";
      this.errorDetails = [];
      this.successMessage = "";
    },
    displayCell(value) {
      if (value instanceof Date) return value.toLocaleDateString();
      return value == null ? "" : String(value);
    },
  },
};
</script>

<style scoped>
.development-evaluation-import {
  --border: #e2e8f0;
  --text: #1e293b;
  --muted: #64748b;
}

.page-heading,
.preview-heading,
.actions {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.page-heading {
  margin-bottom: 16px;
}

.page-heading h2 {
  color: var(--text);
  font-size: 20px;
  margin: 0;
}

.page-heading p {
  color: var(--muted);
  margin: 5px 0 0;
}

.import-card {
  border: 1px solid var(--border);
  border-radius: 12px;
}

.file-label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
}

.template-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
}

.template-note {
  color: #9a3412;
  flex: 1 1 420px;
  font-size: 13px;
  margin: 0;
}

.required {
  color: #dc3545;
}

.file-summary {
  align-items: center;
  background: #f8fafc;
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text);
  display: flex;
  gap: 9px;
  margin-top: 18px;
  padding: 12px 14px;
}

.file-summary i {
  color: #3b82f6;
  font-size: 20px;
}

.preview-heading {
  margin: 24px 0 10px;
}

.preview-heading h4 {
  color: var(--text);
  font-size: 16px;
  margin: 0;
}

.preview-heading span,
.preview-note {
  color: var(--muted);
  font-size: 13px;
}

.preview-table-wrap {
  border: 1px solid var(--border);
  border-radius: 8px;
  max-height: 440px;
  overflow: auto;
}

.preview-table {
  margin: 0;
  min-width: 1500px;
}

.preview-table th {
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 1;
}

.preview-table td,
.preview-table th {
  max-width: 300px;
  overflow-wrap: anywhere;
  vertical-align: top;
}

.preview-note {
  margin: 8px 0 0;
}

.actions {
  justify-content: flex-start;
  margin-top: 22px;
}

.validation-errors {
  max-height: 280px;
  overflow: auto;
  padding-left: 22px;
}

.loading-state {
  color: var(--muted);
  margin-top: 14px;
}

@media (max-width: 767px) {
  .page-heading,
  .preview-heading {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
